/**
 * speakerToken — resolve `[@speaker,emotion]` tokens the way the backend does.
 *
 * The Director serializes a dialogue speaker as `[@{char},{emotion}]` with no
 * escaping (`director_tools.py:_format_dialogue_line`). When `char` is a
 * connected voice-profile name that itself contains a comma — e.g.
 * `Ba của Tú (Tu's Father, voice only)` — splitting at the first comma invents
 * a `voice_role_id` that no voice profile matches, causing the wrong voice to
 * be selected (T284797107 / RFC-1203).
 *
 * RFC-1203 fixed `VideoGeneratorAgent._resolve_dialogue_speaker`. This module is
 * its console mirror for legacy dialogue parsing and any prompt preview UI that
 * needs the same speaker-token behavior.
 *
 * Keep in lockstep with `_resolve_dialogue_speaker` in
 * `agentic_workflow_backend/src/agentic_workflow/agents/video_generator/agent.py`.
 * `tests/fixtures/dialogue_speaker_tokens.json` is the shared case table and is
 * asserted by both `speakerToken.parity.test.ts` and
 * `tests/test_dialogue_speaker_token_parity.py`.
 */

/** Connected profile names, indexed for the two matching passes. */
export interface SpeakerNameIndex {
  rawNames: readonly string[]
  byKey: ReadonlyMap<string, ReadonlySet<string>>
  byNfcKey: ReadonlyMap<string, ReadonlySet<string>>
}

export const EMPTY_SPEAKER_NAME_INDEX: SpeakerNameIndex = {
  rawNames: [],
  byKey: new Map(),
  byNfcKey: new Map(),
}

/**
 * `String.trim()` and Python's `str.strip()` disagree at the edges: Python
 * strips U+001C–U+001F and U+0085, JS strips U+FEFF. A profile key that trims
 * differently on the two sides is exactly the drift this module exists to
 * prevent, so both sides use this explicit union (backend: SPEAKER_TRIM_CHARS).
 */
const SPEAKER_TRIM_CHARS =
  '\t\n\v\f\r \u001c\u001d\u001e\u001f\u0085\u00a0' +
  '\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a' +
  '\u2028\u2029\u202f\u205f\u3000\ufeff'

const SPEAKER_TRIM_RE = new RegExp(`^[${SPEAKER_TRIM_CHARS}]+|[${SPEAKER_TRIM_CHARS}]+$`, 'g')

export function trimSpeaker(value: string): string {
  return value.replace(SPEAKER_TRIM_RE, '')
}

/** Collapse whitespace around commas — the primary comparison key. */
export function normalizeCommaWhitespace(value: string): string {
  return trimSpeaker(value)
    .split(',')
    .map((part) => trimSpeaker(part))
    .join(',')
}

/**
 * Comma-whitespace normalization plus Unicode NFC.
 *
 * Vietnamese and Hangul names can reach the prompt and the profile in different
 * normal forms. Used ONLY as a widening second pass, never as the primary key.
 */
export function normalizeCommaWhitespaceNfc(value: string): string {
  return normalizeCommaWhitespace(value).normalize('NFC')
}

export function buildKnownSpeakerIndex(names: readonly string[]): SpeakerNameIndex {
  const rawNames: string[] = []
  const byKey = new Map<string, Set<string>>()
  const byNfcKey = new Map<string, Set<string>>()
  for (const name of names) {
    const comparisonName = trimSpeaker(name)
    if (!comparisonName) continue
    rawNames.push(name)
    const key = normalizeCommaWhitespace(comparisonName)
    const nfcKey = normalizeCommaWhitespaceNfc(comparisonName)
    if (!byKey.has(key)) byKey.set(key, new Set())
    byKey.get(key)!.add(name)
    if (!byNfcKey.has(nfcKey)) byNfcKey.set(nfcKey, new Set())
    byNfcKey.get(nfcKey)!.add(name)
  }
  return { rawNames, byKey, byNfcKey }
}

/**
 * Mirror of `resolve_name_against_candidates` (voice_name_match.py): the
 * equivalence the backend voice map itself uses — case-insensitive first match,
 * then a UNIQUE trim-insensitive match. Ambiguous stripped forms resolve to
 * nothing so the caller keeps its loud failure instead of guessing a voice.
 */
export function resolveNameAgainstCandidates(
  name: string,
  candidates: readonly string[],
): string | null {
  if (!name) return null
  const nameLower = name.toLowerCase()
  for (const candidate of candidates) {
    if (candidate.toLowerCase() === nameLower) return candidate
  }
  const nameStripped = trimSpeaker(name).toLowerCase()
  if (!nameStripped) return null
  const strippedMatches = candidates.filter(
    (c) => trimSpeaker(c).toLowerCase() === nameStripped,
  )
  return strippedMatches.length === 1 ? strippedMatches[0] : null
}

/** Text after the comma that terminates a known profile-name prefix. */
function emotionAfterProfilePrefix(rawSpeaker: string, profileName: string): string {
  const profileCommas = (profileName.match(/,/g) ?? []).length
  let commasSeen = 0
  for (let i = 0; i < rawSpeaker.length; i += 1) {
    if (rawSpeaker[i] !== ',') continue
    if (commasSeen === profileCommas) return trimSpeaker(rawSpeaker.slice(i + 1))
    commasSeen += 1
  }
  // Unreachable: the caller only calls this after a `${name},` prefix match, and
  // normalization never changes the comma count. Fail loudly rather than
  // silently returning a wrong emotion.
  throw new Error('Dialogue speaker/profile normalization invariant violated')
}

function matchKnownName(
  rawSpeaker: string,
  normalizedSpeaker: string,
  normalizedNames: ReadonlyMap<string, ReadonlySet<string>>,
): { character: string; emotion: string } | null {
  const exactNames = normalizedNames.get(normalizedSpeaker)
  if (exactNames && exactNames.size === 1) {
    return { character: [...exactNames][0], emotion: '' }
  }

  let best: { key: string; character: string } | null = null
  for (const [normalizedName, canonicalNames] of normalizedNames) {
    if (canonicalNames.size !== 1) continue
    if (!normalizedSpeaker.startsWith(`${normalizedName},`)) continue
    if (best === null || normalizedName.length > best.key.length) {
      best = { key: normalizedName, character: [...canonicalNames][0] }
    }
  }
  if (best === null) return null
  return { character: best.character, emotion: emotionAfterProfilePrefix(rawSpeaker, best.key) }
}

/** Split at the FIRST comma; everything after it is the emotion. */
export function legacySplitSpeaker(rawSpeaker: string): { character: string; emotion: string } {
  const commaIdx = rawSpeaker.indexOf(',')
  if (commaIdx < 0) return { character: trimSpeaker(rawSpeaker), emotion: '' }
  return {
    character: trimSpeaker(rawSpeaker.slice(0, commaIdx)),
    emotion: trimSpeaker(rawSpeaker.slice(commaIdx + 1)),
  }
}

export type SpeakerResolution = {
  character: string
  emotion: string
  /** Which rule produced this answer — surfaced to NAD logs, not to users. */
  source: 'known-name' | 'known-name-nfc' | 'legacy'
}

/**
 * Resolve a raw `[@...]` token body against the connected profile names.
 *
 * Order matches the backend exactly: known-name match on the primary key, then
 * a widening NFC pass that may only fire when the legacy reading does NOT
 * already name a profile, then the legacy split. The NFC gate is what keeps the
 * widening from stealing a resolution that works today.
 */
export function resolveDialogueSpeaker(
  rawSpeaker: string,
  index: SpeakerNameIndex,
): SpeakerResolution {
  const hit = matchKnownName(rawSpeaker, normalizeCommaWhitespace(rawSpeaker), index.byKey)
  if (hit) return { ...hit, source: 'known-name' }

  const legacy = legacySplitSpeaker(rawSpeaker)
  if (resolveNameAgainstCandidates(legacy.character, index.rawNames) === null) {
    const nfcHit = matchKnownName(
      rawSpeaker,
      normalizeCommaWhitespaceNfc(rawSpeaker),
      index.byNfcKey,
    )
    if (nfcHit) return { ...nfcHit, source: 'known-name-nfc' }
  }

  return { ...legacy, source: 'legacy' }
}
